/**
 * PATH: packages/ui/src/kanban/components/KanbanCardFooterIcons.tsx
 * FILE_ID: UI-KANBAN-CARD-FOOTER-ICONS-001
 * PURPOSE: Left triage icon strip for the card footer.
 * ROLE IN ARCHITECTURE: Presentational leaf under KanbanCardFooter.
 * MAIN EXPORTS: KanbanCardFooterIcons
 * NON-RESPONSIBILITIES: Does not decide whether the footer mounts.
 * NOTES FOR FUTURE AI: Hide omits chrome. Paperclip must keep reading
 *   the derived attachmentCount, never related_attachment_ids_json.length.
 */
import {
  AlignLeft,
  CheckSquare,
  CornerDownRight,
  Flag,
  Link2,
  Mail,
  MailOpen,
  Merge,
  MessageSquare,
  Paperclip,
  Reply,
  Table2,
} from 'lucide-react'

import type { Card as KanbanCardModel } from '../types'
import { KanbanCardDuePills } from './KanbanCardDuePills'
import { KanbanCardEntryRollupChip } from './KanbanCardEntryRollupChip'
import { buildPresenceTitle } from './kanbanCardPresence'
import { priorityFlagMeta } from './kanbanCardVisuals'
import {
  footerFieldOn,
  type FooterFieldVisibility,
  type FooterSignals,
} from './kanbanCardFooterSignals'

const META_ICON = 'w-3 h-3 shrink-0 origin-center transition-transform duration-150 hover:scale-[1.6]'

export function KanbanCardFooterIcons({
  card,
  isCompact,
  checklistDone,
  checklistTotal,
  commentCount,
  overdue,
  hovered,
  checklistApiReady,
  checklistExpanded,
  onToggleChecklist,
  onOpenEmail,
  onOpenWorkbook,
  relatedCount,
  unseenCount,
  hideActionPill,
  onSetActionDate,
  onSetDueDate,
  showDue,
  fields,
  signals,
}: {
  card: KanbanCardModel
  isCompact: boolean
  checklistDone: number
  checklistTotal: number
  commentCount: number
  overdue: boolean
  hovered: boolean
  checklistApiReady: boolean
  checklistExpanded: boolean
  onToggleChecklist: () => void
  onOpenEmail?: (card: KanbanCardModel) => void
  onOpenWorkbook?: (card: KanbanCardModel) => void
  relatedCount: number
  unseenCount: number
  hideActionPill?: boolean
  onSetActionDate?: (
    card: KanbanCardModel,
    actionDate: string | null,
  ) => Promise<unknown> | void
  onSetDueDate?: (
    card: KanbanCardModel,
    dueDate: string | null,
  ) => Promise<unknown> | void
  showDue?: boolean
  fields?: FooterFieldVisibility
  signals: FooterSignals
}) {
  const on = (id: Parameters<typeof footerFieldOn>[1]) => footerFieldOn(fields, id)
  const {
    attachmentCount,
    relatedEmailCount,
    childCount,
    mergedCount,
    presenceCount,
    hasWorkbook,
  } = signals
  const presenceTitle = buildPresenceTitle(card)
  const pf = on('priority') ? priorityFlagMeta(card.priority) : null

  return (
    <div className="flex items-center gap-2 min-w-0 flex-wrap">
      {pf && (
        <span
          className="inline-flex items-center gap-0.5 font-semibold"
          style={{ color: pf.color }}
          title={`Priority: ${pf.label}`}
          aria-label={`Priority: ${pf.label}`}
        >
          <Flag className="w-3 h-3 shrink-0 fill-current" />
          {!isCompact && <span>{pf.label}</span>}
        </span>
      )}
      {on('personalCopy') && card.is_personal_mirror && (
        <span
          className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 font-medium"
          title="Your personal copy of a team card — schedule it here; labels, members and chat are shared with the team. Open to manage them."
          aria-label="Personal copy of a team card"
        >
          <Link2 className="w-3 h-3" />
          Personal copy
        </span>
      )}
      {on('mail') &&
        card.primary_email &&
        (onOpenEmail ? (
          <button
            type="button"
            aria-label="Open the linked email"
            title="Open the linked email"
            onClick={(e) => {
              e.stopPropagation()
              onOpenEmail(card)
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="inline-flex items-center rounded px-0.5 py-0.5 text-neutral-500 transition-colors hover:bg-neutral-200/70 hover:text-blue-600 dark:text-zinc-400 dark:hover:bg-zinc-800/70 dark:hover:text-blue-400"
          >
            <Mail className={META_ICON} />
          </button>
        ) : (
          <Mail className={META_ICON} aria-label="Has email" />
        ))}
      {relatedEmailCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5"
          title={`${relatedEmailCount} thread reply(ies)`}
        >
          <Reply className={META_ICON} />
          {relatedEmailCount}
        </span>
      )}
      {on('description') && card.summary && (
        <AlignLeft className={META_ICON} aria-label="Has description" />
      )}
      {attachmentCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5"
          title={`${attachmentCount} attachments`}
        >
          <Paperclip className={META_ICON} />
          {attachmentCount}
        </span>
      )}
      {on('checklist') && checklistTotal > 0 && checklistApiReady && (
        <ChecklistChip
          checklistDone={checklistDone}
          checklistTotal={checklistTotal}
          checklistExpanded={checklistExpanded}
          onToggleChecklist={onToggleChecklist}
        />
      )}
      {on('checklist') && checklistTotal > 0 && !checklistApiReady && (
        <span
          className={[
            'inline-flex items-center gap-0.5',
            checklistDone === checklistTotal
              ? 'text-emerald-600 dark:text-emerald-400 font-medium'
              : '',
          ].join(' ')}
          title={`${checklistDone} of ${checklistTotal} complete`}
        >
          <CheckSquare className={META_ICON} />
          {checklistDone}/{checklistTotal}
        </span>
      )}
      {on('checklist') && checklistTotal === 0 && checklistApiReady && hovered && (
        <button
          type="button"
          aria-label="Add a checklist"
          aria-expanded={checklistExpanded}
          onClick={(e) => {
            e.stopPropagation()
            onToggleChecklist()
          }}
          className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10.5px] text-neutral-500 transition-colors hover:bg-neutral-200/70 hover:text-neutral-700 dark:text-zinc-400 dark:hover:bg-zinc-800/70 dark:hover:text-zinc-100"
          title="Add a checklist to this card"
        >
          <CheckSquare className={META_ICON} />
          <span>Checklist</span>
        </button>
      )}
      {on('comments') && commentCount > 0 && (
        <span className="inline-flex items-center gap-0.5" title={`${commentCount} comments`}>
          <MessageSquare className={META_ICON} />
          {commentCount}
        </span>
      )}
      {childCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5"
          title={`${childCount} sub-card${childCount === 1 ? '' : 's'}`}
          aria-label={`Has ${childCount} sub-card${childCount === 1 ? '' : 's'}`}
        >
          <CornerDownRight className={META_ICON} />
          {childCount}
        </span>
      )}
      {on('rollup') && <KanbanCardEntryRollupChip card={card} />}
      {hasWorkbook &&
        (onOpenWorkbook ? (
          <button
            type="button"
            aria-label="Open the card's workbook"
            title="Has a workbook — click to open"
            onClick={(e) => {
              e.stopPropagation()
              onOpenWorkbook(card)
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="inline-flex items-center rounded px-0.5 py-0.5 text-emerald-600 transition-colors hover:bg-neutral-200/70 dark:text-emerald-400 dark:hover:bg-zinc-800/70"
          >
            <Table2 className={META_ICON} />
          </button>
        ) : (
          <span title="Has a workbook" aria-label="Has a workbook">
            <Table2 className={`${META_ICON} text-emerald-600 dark:text-emerald-400`} />
          </span>
        ))}
      {on('related') && relatedCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5 text-violet-600 dark:text-violet-300"
          title={`${relatedCount} related card${relatedCount === 1 ? '' : 's'} on this board (same thread or job)`}
          aria-label={`${relatedCount} related card${relatedCount === 1 ? '' : 's'}`}
        >
          <Link2 className="w-3 h-3" />
          {relatedCount}
        </span>
      )}
      {on('unseen') && unseenCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5 font-medium text-sky-600 dark:text-sky-300"
          title={`${unseenCount} email${unseenCount === 1 ? '' : 's'} on this card you haven't seen (and aren't in your own inbox)`}
          aria-label={`${unseenCount} unseen email${unseenCount === 1 ? '' : 's'}`}
        >
          <MailOpen className="w-3 h-3" />
          {unseenCount}
        </span>
      )}
      {mergedCount > 0 && (
        <span
          className="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400"
          title={`${mergedCount} duplicate card${mergedCount === 1 ? '' : 's'} merged into this one (archived) — open the card and see Merged in / Activity`}
          aria-label={`${mergedCount} duplicate card${mergedCount === 1 ? '' : 's'} merged in`}
        >
          <Merge className="w-3 h-3" />
          +{mergedCount} merged
        </span>
      )}
      {presenceCount > 1 && (
        <span
          className="inline-flex items-center gap-0.5 font-medium text-blue-600 dark:text-blue-400"
          title={presenceTitle}
          aria-label={presenceTitle}
          data-testid="kanban-card-linked-chip"
        >
          <span aria-hidden>⧉</span>
          {presenceCount}
        </span>
      )}
      <KanbanCardDuePills
        card={card}
        overdue={overdue}
        hideActionPill={hideActionPill || !on('actionDate')}
        hideDuePill={!showDue || !on('due')}
        hidePreviousDue={!on('previousDue')}
        hidePreviousAction={!on('previousAction')}
        hideSnooze={!on('snooze')}
        onSetActionDate={on('actionDate') ? onSetActionDate : undefined}
        onSetDueDate={showDue && on('due') ? onSetDueDate : undefined}
      />
    </div>
  )
}

function ChecklistChip({
  checklistDone,
  checklistTotal,
  checklistExpanded,
  onToggleChecklist,
}: {
  checklistDone: number
  checklistTotal: number
  checklistExpanded: boolean
  onToggleChecklist: () => void
}) {
  return (
    <button
      type="button"
      aria-label={
        checklistExpanded
          ? 'Hide checklist'
          : `Show checklist (${checklistDone} of ${checklistTotal} complete)`
      }
      aria-expanded={checklistExpanded}
      onClick={(e) => {
        e.stopPropagation()
        onToggleChecklist()
      }}
      className={[
        'inline-flex items-center gap-0.5 rounded px-0.5 py-0.5 transition-colors hover:bg-neutral-200/70 dark:hover:bg-zinc-800/70',
        checklistDone === checklistTotal
          ? 'text-emerald-600 dark:text-emerald-400 font-medium'
          : '',
      ].join(' ')}
      title={`${checklistDone} of ${checklistTotal} complete — click to ${checklistExpanded ? 'hide' : 'expand'}`}
    >
      <CheckSquare className={META_ICON} />
      {checklistDone}/{checklistTotal}
    </button>
  )
}
